import { effect, inject, Injectable, signal, WritableSignal } from "@angular/core";
import { Observable, ObservableInput, combineLatest, map, takeUntil, EMPTY, tap, switchMap, catchError, throwError, finalize, ObservableInputTuple } from "rxjs";
import { GlobalSpinnerService } from "../shared/global-spinner/global-spinner.service";

@Injectable({
    providedIn: 'root'
})
export class LoadingManager {
    #loadingService: GlobalSpinnerService = inject(GlobalSpinnerService);
    spinnerSignal: WritableSignal<number> = signal(0);

    spinnerEffect = effect(() => {
        if (this.spinnerSignal() == 0)
            this.#loadingService.hide();
        else
            this.#loadingService.show();
    });

    observeSingle<R, O>(refreshers: Observable<O>, source: (value: O) => Observable<R>
        , takeUntilFunction: ObservableInput<never> | null = null
        , withGlobalSpinner = true): Observable<R> {
        let loadingToken = 0;
        return combineLatest([refreshers]).pipe(
            map((x: readonly O[]) => x[0])
            , takeUntil(takeUntilFunction ?? EMPTY)
            , tap(() => {
                if (loadingToken == 0) return;
                this.spinnerSignal.update(v => {
                    return v - loadingToken;
                });
                loadingToken = 0;
                // the ongoing request will get canceled by the browser, rxjs will have no way of knowing it    
            }),
            tap(() => {
                if (!withGlobalSpinner) return;
                loadingToken += 1
                this.spinnerSignal.update(v => {
                    return v + loadingToken
                })
            }),
            switchMap(source)
            , catchError((err, _) => {
                if (!withGlobalSpinner) return throwError(() => err);

                this.spinnerSignal.update(v => {
                    return v - loadingToken;
                });
                loadingToken -= 1;

                return throwError(() => err);
            })
            , tap(() => {
                if (!withGlobalSpinner) return;

                this.spinnerSignal.update(v => {
                    return v - loadingToken;
                });
                loadingToken -= 1;
            })
            , finalize(() => {
                if (!withGlobalSpinner) return;

                this.spinnerSignal.update(v => {
                    return v - loadingToken;
                })
                loadingToken -= 1;
            })

        )

    }

    observe<R, O extends readonly unknown[]>(refreshers: readonly [...ObservableInputTuple<O>], source: (value: O) => Observable<R>
        , takeUntilFunction: ObservableInput<never> | null = null
        , withGlobalSpinner = true): Observable<R> {
        let loadingToken = 0;

        return combineLatest(refreshers).pipe(
            takeUntil(takeUntilFunction ?? EMPTY)
            , tap(() => {
                if (loadingToken == 0) return;
                this.spinnerSignal.update(v => {
                    return v - loadingToken;
                });
                loadingToken = 0;
                // the ongoing request will get canceled by the browser, rxjs will have no way of knowing it    
            }),
            tap(() => {
                if (!withGlobalSpinner) return;
                loadingToken += 1
                this.spinnerSignal.update(v => {
                    return v + loadingToken
                })
            }),
            switchMap(source)
            , catchError((err, _) => {
                if (!withGlobalSpinner) return throwError(() => err);

                this.spinnerSignal.update(v => {
                    return v - loadingToken;
                });
                loadingToken -= 1;

                return throwError(() => err);
            })
            , tap(() => {
                if (!withGlobalSpinner) return;

                this.spinnerSignal.update(v => {
                    return v - loadingToken;
                });
                loadingToken -= 1;
            })
            , finalize(() => {
                if (!withGlobalSpinner) return;

                this.spinnerSignal.update(v => {
                    return v - loadingToken;
                })
                loadingToken -= 1;
            })

        )

    }

}